import {
  useState, useEffect,
} from 'react';
import {
  Button,
  Checkbox,
  Form,
  Modal,
  Spin,
} from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRequest } from 'ahooks';
import qs from 'query-string';
import moment from 'moment/moment';
import { useSelector } from 'react-redux';
import { getArea, getDictType } from '@/services/common';

import {
  addApply, editApply, getDeclarationData,
} from '@/services/agriculturalSubsidies';
import SubjectBasicInfo from '@/pages/agriculturalSubsidies/Application/components/SubjectBasicInfo';
import ProjectBasicInfo from '@/pages/agriculturalSubsidies/Application/components/ProjectBasicInfo';
import useKeepAlive from '@/hooks/useKeepAlive';
import OtherUpload from '@/pages/agriculturalSubsidies/Application/components/OtherUpload';
import { SPECIAL_PROJECT_NAME, SPECIAL_AGRICULTURAL_TYPE, SPECIAL_ENTER_NAME } from '@/utils/mappingDict';
import { getFileList } from '@/utils/fileDataUtil';

const formItemLayout = {
  labelCol: {
    sm: { span: 8 },
  },
  wrapperCol: {
    sm: { span: 24 },
  },
};

const defaultParams = [SPECIAL_PROJECT_NAME, SPECIAL_AGRICULTURAL_TYPE, SPECIAL_ENTER_NAME].join(',');

export default function Application() {
  const [form] = Form.useForm();
  const Navigate = useNavigate();
  const { search, state: passedState } = useLocation();
  const { id: addId, specialAgriculturalId: editId } = qs.parse(search);
  const user = useSelector((state) => state.userInfo);

  const [authInfoModal, setAuthInfoModal] = useState(false); // 授权书内容

  const { data: dict = {} } = useRequest(getDictType, { defaultParams: [defaultParams] });
  const { data: area = [] } = useRequest(getArea); // 区域

  // 数据回显
  const { data: detailData, loading, run } = useRequest(getDeclarationData, {
    manual: true,
    onSuccess(res) {
      const {
        projectProvinceId, projectCityId, projectAreaId, provinceId, cityId, areaId, registrationTime, projectConstructionPeriodStart, projectConstructionPeriodEnd,
      } = res;
      form.setFieldsValue({
        ...res,
        projectNature: res.projectNature?.split(','),
        projectConstructionPeriodStart: projectConstructionPeriodStart && [moment(projectConstructionPeriodStart, 'YYYY-MM'), moment(projectConstructionPeriodEnd, 'YYYY-MM')],
        registrationTime: moment(registrationTime, 'YYYY-MM-DD'),
        ignore: {
          projectRegion: [projectProvinceId, projectCityId, projectAreaId],
          registrationLocation: [provinceId, cityId, areaId],
          constructionUnit: getFileList(res?.constructionUnit),
          studyReport: getFileList(res?.studyReport),
          leaseAgreement: getFileList(res?.leaseAgreement),
          approvalDocuments: getFileList(res?.approvalDocuments),
          policyDocument: getFileList(res?.policyDocument),
          otherDocument: getFileList(res?.otherDocument),
          projectApplication: getFileList(res?.projectApplication),
          businessLicense: getFileList(res?.businessLicense),
          leadingEnterprises: getFileList(res?.leadingEnterprises),
          mainBusiness: getFileList(res?.mainBusiness),
          previousYear: getFileList(res?.previousYear),
          processingFacilities: getFileList(res?.processingFacilities),
          linkingFarmers: getFileList(res?.linkingFarmers),
        },
      });
    },
  });

  // 添加
  const { loading: addLoading, run: add } = useRequest(addApply, {
    manual: true,
    onSuccess() {
      Navigate('/result');
    },
  });

  // 修改
  const { loading: editLoading, run: editRun } = useRequest(editApply, {
    manual: true,
    onSuccess() {
      Navigate('/result');
    },
  });

  // 保证token不过期
  useKeepAlive();

  useEffect(() => {
    if (editId) {
      run(editId);
    } else {
      form.setFieldsValue({
        applicantName: user?.displayName,
        applicantNo: user?.displayCode,
        projectUnit: user?.displayName,
        declarationYear: passedState?.year,
      });
    }
  }, []);

  function onFinish() {
    form.validateFields().then((values) => {
      const { ignore: { projectRegion, registrationLocation }, projectConstructionPeriodStart } = values;
      const obj = {
        ...values,
        projectProvinceId: projectRegion?.[0],
        projectCityId: projectRegion?.[1],
        projectAreaId: projectRegion?.[2],
        provinceId: registrationLocation?.[0],
        cityId: registrationLocation?.[1],
        areaId: registrationLocation?.[2],
        registrationTime: moment(values.registrationTime).format('YYYY-MM-DD'),
        specialAgriculturalId: addId,
        id: editId,
        ignore: undefined,
        projectConstructionPeriodStart: moment(projectConstructionPeriodStart[0]).format('YYYY-MM'),
        projectConstructionPeriodEnd: moment(projectConstructionPeriodStart[1]).format('YYYY-MM'),
        projectNature: Array.isArray(values.projectNature) ? values.projectNature.join(',') : values.projectNature,
      };
      if (editId) {
        editRun(obj);
      } else {
        add(obj);
      }
    }).catch(() => {
      setTimeout(() => {
        const errorList = document.querySelectorAll('.ant-form-item-has-error');
        errorList[0]?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }, 500);
    });
  }

  function checkAuthorize(_, value) {
    if (!value) {
      return Promise.reject(new Error('勾选同意并接受'));
    }
    return Promise.resolve();
  }

  return (
    <div className="major-financing-application">
      <div className="content-width">
        <Spin spinning={loading}>
          <div className="margin-top20">
            <Form
              {...formItemLayout}
              form={form}
              name="form"
              className="form"
              onFinish={onFinish}
            >
              <SubjectBasicInfo
                area={area}
                data={detailData}
                dict={dict}
              />
              <ProjectBasicInfo
                formRef={form}
                area={area}
                dict={dict}
                data={detailData}
              />
              <OtherUpload formRef={form} />
              <div className="authorize">
                <Form.Item
                  required
                  name="authorize"
                  wrapperCol={{ span: 24 }}
                  valuePropName="checked"
                  rules={[
                    { validator: checkAuthorize },
                  ]}
                >
                  <Checkbox
                    onChange={(e) => {
                      form.setFieldsValue({ authorize: false });
                      if (e.target.checked) {
                        setAuthInfoModal(true);
                      }
                    }}
                  >
                    同意并接受
                    <Button
                      type="link"
                      className="authorize-name"
                      onClick={() => setAuthInfoModal(true)}
                    >
《承诺函》
                    </Button>
                  </Checkbox>
                </Form.Item>
              </div>
              <div className="submit-btn-box">
                <Button type="primary" onClick={onFinish} loading={addLoading || editLoading}>
                  提交申请
                </Button>
              </div>
            </Form>
            <Modal
              title="承诺函"
              open={authInfoModal}
              footer={
                [
                  <Button key="skip" onClick={() => setAuthInfoModal(false)}>
                    取消
                  </Button>,
                  <Button
                    type="primary"
                    key="confirm"
                    onClick={() => {
                      setAuthInfoModal(false);
                      form.setFieldsValue({ authorize: true });
                    }}
                  >
                    确认
                  </Button>,
                ]
              }
              onCancel={() => setAuthInfoModal(false)}
            >
              <div>
                本企业承诺所有申报材料真实、合法、有效，若发现材料存在虚假、瞒报等情况，本企业自愿放弃本次申报，并退还获得的财政补贴资金，并承担违规违法责任。
              </div>
            </Modal>
          </div>
        </Spin>

      </div>
    </div>
  );
}
